<template>
    <div>
        <x-data-table
            class="no-row-hover"
            :headers="headers"
            :items="amendments"
            hide-actions
        >
            <template v-slot:no-data>
                <p class="error-message text-xs-center">No amendments made</p>
            </template>

            <template v-slot:items="props">
                <td>
                    {{ props.item.effective_date | formatDate }}
                </td>

                <td>
                    {{ props.item.reference }}
                </td>

                <td>
                    {{ props.item.type | snakeToTitle }}
                </td>

                <td>
                    Request: {{ props.item.type | snakeToTitle }}
                    <div v-if="props.item.settlement_due_date">
                        Settlement due date: {{ props.item.settlement_due_date | formatDate }}
                    </div>
                </td>

                <td>
                    N/A
                </td>

                <td>
                    <status-chip
                        class="ma-0"
                        small
                        :status="props.item.status"
                    />

                    <action-icon
                        v-if="props.item.status === 'pending'"
                        color="grey-9"
                        icon="edit"
                        icon-color="blue"
                    >
                        <div
                            class="amendment-request-approval"
                            v-for="request in props.item.endorsement_requests"
                            :key="`endorsement-request-${request.id}`"
                        >
                            <span>{{ getInsurerCompany(request).name }}</span>
                            <status-chip small :status="request.status === 'captive_reviewing' ? 'pending' : request.status" />
                        </div>
                    </action-icon>
                </td>

                <td class="text-xs-right">
                    <template v-if="showModalReview(props.item)">
                        <x-dialog v-model="dialog[props.item.id]" persistent width="900" max-width="900">
                            <template v-slot:activator="{ on }">
                                <x-btn color="tertiary" small v-on="on">View amendment</x-btn>
                            </template>

                            <x-container>
                                <amendment-review-list
                                    :declaration="declaration"
                                >
                                    <x-layout class="response-actions">
                                        <x-flex xs12 class="text-xs-center">
                                            <x-btn
                                                class="ma-2"
                                                color="tertiary"
                                                small
                                                @click.stop="$set(dialog, props.item.id, false)"
                                            >
                                                Close
                                            </x-btn>
                                        </x-flex>
                                    </x-layout>
                                </amendment-review-list>
                            </x-container>
                        </x-dialog>
                    </template>

                    <template v-if="showViewDocumentsModal(props.item)">
                        <x-dialog v-model="dialog[props.item.id]" width="900" max-width="900">
                            <template v-slot:activator="{ on }">
                                <x-btn color="tertiary" small v-on="on">View documents</x-btn>
                            </template>

                            <x-container>
                                <document-list
                                    v-if="props.item.id"
                                    :documentable-id="props.item.id"
                                    documentable-type="endorsement"
                                    :can-delete="() => false"
                                />
                            </x-container>
                        </x-dialog>
                    </template>

                    <x-btn
                        v-if="canUpdateInsurerApprovals(props.item)"
                        class="square-icon"
                        color="tertiary"
                        small
                        @click="updateInsurerApprovals(props.item)"
                    >
                        <i class="material-icons">how_to_reg</i>
                    </x-btn>

                    <x-btn
                        v-if="amendmentIsPendingOrInProgress(props.item) || amendmentIsDraft(props.item)"
                        class="square-icon"
                        color="tertiary"
                        small
                        @click="cancel(props.item)"
                    >
                        <i class="material-icons">delete</i>
                    </x-btn>

                    <x-btn
                        v-if="amendmentIsDraft(props.item)"
                        class="square-icon ml-2"
                        color="tertiary"
                        small
                        @click="goToAmendment"
                    >
                        <i class="material-icons">preview</i>
                    </x-btn>
                </td>
            </template>
        </x-data-table>

        <modal
            ref="cancel"
            title="Cancel amendment"
            confirmation
        >
            <p>Are you sure you would like to cancel the pending amendment?</p>
        </modal>

        <modal
            ref="updateInsurerApprovals"
            title="Update insurer approvals"
            confirmation
            action-text="Update"
            :action-disabled="this.approvingInsurerIds.length === 0"
        >
            <x-layout justify-center text-xs-left>
                <x-flex xs9>
                    <p>This amendment will only be visible to the selected insurers.</p>
                    <p>Any changes made to the risk via amendment will be still visible to all insurers once the amendment is applied.</p>
                </x-flex>
            </x-layout>

            <x-layout justify-center>
                <x-flex xs6>
                    <x-select
                        label="Require approval from"
                        :items="allApprovingInsurers"
                        v-model="approvingInsurerIds"
                        placeholder="Require approval from"
                        append-icon="keyboard_arrow_down"
                        outline
                        item-text="name"
                        item-value="id"
                        :item-disabled="({is_new_insurer}) => is_new_insurer"
                        multiple
                        hide-details
                        data-test="approvers"
                        :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                    >
                        <template slot="selection" slot-scope="{item, index}">
                            <span v-if="index === 0">{{ numberOfInsurersSelected }}</span>
                        </template>

                        <template slot="prepend-item">
                            <x-list-tile class="buttons-top-tile no-hover">
                                <x-btn @click="selectAllInsurers" color="secondary">Select all</x-btn>
                                <x-btn data-test="deselect_all" @click="deselectAllInsurers" color="tertiary">Deselect all</x-btn>
                            </x-list-tile>
                        </template>
                    </x-select>
                </x-flex>
            </x-layout>
        </modal>
    </div>
</template>

<script>
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import ActionIcon from '~/components/Shared/UIComponents/ActionIcon';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import AmendmentReviewList from "~/components/Shared/UIComponents/AmendmentReviewList";
    import DocumentList from '~/components/Shared/UIComponents/DocumentList';
    import submissionHelpers from '~/mixins/submissionHelpers';

    export default {
        name: 'AmendmentsTable',
        mixins: [submissionHelpers],
        components: {
            StatusChip,
            ActionIcon,
            AmendmentReviewList,
            Modal,
            DocumentList
        },
        props: {
            endorsements: {
                type: Array,
                default: []
            },
            declaration: {
                type: Object,
                default: []
            },
            insurerList: {
                type: Array,
                default: () => []
            },
        },
        data() {
            return {
                headers: [
                    {
                        text: 'Effective Date',
                        value: 'effective_date',
                        width: '10%',
                    },
                    {
                        text: 'Reference',
                        value: 'reference',
                        width: '5%',
                    },
                    {
                        text: 'Type',
                        value: 'type'
                    },
                    {
                        text: 'Description',
                        value: 'type'
                    },
                    {
                        text: 'Premium change',
                        value: ''
                    },
                    {
                        text: 'Status',
                        value: 'status',
                        sortable: false,
                        width: '10%',
                    },
                    {
                        text: 'Actions',
                        sortable: false,
                        align: 'right',
                        width: '25%',
                    }
                ],
                dialog: {},
                selectedAmendmentForDocumentUploadId: null,
                approvingInsurerIds: [],
                allApprovingInsurers: [],
            }
        },
        computed: {
            numberOfInsurersSelected() {
                if (this.allApprovingInsurers.length === this.approvingInsurerIds.length) {
                    return "All insurers";
                }
                return this.approvingInsurerIds.length === 1
                    ? "1 insurer"
                    : this.approvingInsurerIds.length + " insurers";
            },
            amendments() {
                return this.endorsements.filter(e => ['off_risk', 'amendment'].includes(e.type));
            },
            idle() {
                return this.status === 'idle';
            },
        },
        methods: {
            async cancel(amendment) {
                if (await this.$refs.cancel.open() === false) {
                    return;
                }

                this.$emit('cancelAmendment', amendment);
            },
            async updateInsurerApprovals(amendment) {
                const newLines = amendment.field_changes.new_lines ?? [];
                const newInsurerIds = newLines.map(({insurer_company_id}) => insurer_company_id);
                this.allApprovingInsurers = amendment.endorsement_requests.map(({insurer_company}) => ({
                    ...insurer_company,
                    is_new_insurer: newInsurerIds.includes(insurer_company.id)
                }));
                this.approvingInsurerIds = amendment.endorsement_requests
                    .filter(({status}) => status !== "not_required")
                    .map(({insurer_company_id}) => insurer_company_id);
                if (await this.$refs.updateInsurerApprovals.open() === false) {
                    return;
                }

                this.$emit('updateInsurerApprovals', amendment, this.approvingInsurerIds);
            },
            selectAllInsurers() {
                this.approvingInsurerIds = this.allApprovingInsurers.map(({id}) => id);
            },
            deselectAllInsurers() {
                this.approvingInsurerIds = this.allApprovingInsurers.filter(({is_new_insurer}) => is_new_insurer).map(insurer => insurer.id);
            },
            showModalReview(endorsement) {
                if (endorsement.type !== 'amendment' || !this.amendmentIsPendingOrInProgress(endorsement)) {
                    return false;
                }
                return true;
            },
            getInsurerCompany(request) {
                if (request.captive_insurer_company) {
                    return request.captive_insurer_company;
                }
                return request.insurer_company;
            },
            canUpdateInsurerApprovals(amendment) {
                return amendment.type === 'amendment' &&
                    this.amendmentIsPendingOrInProgress(amendment)
            },
            amendmentIsPendingOrInProgress(amendment) {
                return this.amendmentIsPending(amendment) || this.amendmentIsInProgress(amendment);
            },
            amendmentIsPending(amendment) {
                return amendment.status === 'pending';
            },
            amendmentIsInProgress(amendment) {
                return amendment.status === 'in_progress';
            },
            amendmentIsDraft(amendment) {
                return amendment.status === 'draft';
            },
            async refreshDocumentsForAmendment(amendment) {
                const res = await axios.get(`/api/documents/endorsement/${amendment.id}`);
                const amend = this.amendments.find(amm => amm.id === amendment.id);
                amend.documents = res.data.data || [];
            },
            goToAmendment() {
                this.$router.push({path: `/agent/amendment-entry/${this.declaration.submission_id}`});
            },
            async cancelDraft(amendment) {
                await axios.put(`/api/broker/declaration/${this.declaration.submission_id}/amendment/${amendment.id}/cancel`).then((response) => {
                        amendment.status = response.data.data.status;
                    }
                );
            },
            showViewDocumentsModal(endorsement) {
                return ['approved', 'applied'].includes(endorsement.status);
            },
        }
    }
</script>

<style lang="scss" scoped>
    .amendment-request-approval {
        display: flex;
        justify-content: space-between;
        align-items: center;

        + .amendment-request-approval {
            margin-top: 0.5em;
        }

        .x-tag {
            margin: 0 0 0 1em;
        }
    }

    ::v-deep .x-list--disabled {
        opacity: 0.35;
    }
</style>
